import { useEffect, useState } from "react";
import { t } from "i18next";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import CustomerSelect from "../../../components/data/customer/CustomerSelect";
import "react-datepicker/dist/react-datepicker.css";
import WorkorderStatusSelect from "../../../components/data/workorder-status/WorkorderStatusSelect";
import { createListQuery, createSingleQuery, SearchQuery } from "../../../services/api/search";

interface PlanningFilterFormProps {
  data?: Partial<EasyTech.PlanningFilterType>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

export const getPlanningQueriesFromFilter = (filters: Partial<EasyTech.PlanningFilterType>, isWorkorder: boolean):SearchQuery[] => {
  const queries: SearchQuery[] = [];

  // Pour les technician_times, on utilise toujours les champs de la nouvelle API
  if (filters.technician) {
    if (Array.isArray(filters.technician)) {
      const techIds = filters.technician.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("technician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("technician.id", techIds));
      }
    } else if (typeof filters.technician === "object" && (filters.technician as any).id) {
      queries.push(createSingleQuery("technician.id", (filters.technician as any).id));
    } else if (typeof filters.technician === "string") {
      queries.push(createSingleQuery("technician.id", filters.technician));
    }
  }

  if (filters.customer) {
    // Pour technician_times, le customer est accessible via workOrder.customer.id
    const keyCustomer = "workOrder.customer.id";
    if (Array.isArray(filters.customer)) {
      const customerIds = filters.customer.map((c: any) => typeof c === "string" ? c : c.id);
      if (customerIds.length === 1) {
        queries.push(createSingleQuery(keyCustomer, customerIds[0]));
      } else if (customerIds.length > 1) {
        queries.push(createListQuery(keyCustomer, customerIds));
      }
    } else if (typeof filters.customer === "object" && (filters.customer as any).id) {
      queries.push(createSingleQuery(keyCustomer, (filters.customer as any).id));
    } else if (typeof filters.customer === "string") {
      queries.push(createSingleQuery(keyCustomer, filters.customer));
    }
  }

  if (filters.status && filters.status.length > 0) {
    // Pour technician_times, le status est accessible via workOrder.status
    const keyStatus = "workOrder.status";
    const statusList = Array.isArray(filters.status) ? filters.status : [filters.status];
    queries.push(createListQuery(keyStatus, statusList));
  }
  return queries;
};

const PlanningFilterForm = (props: PlanningFilterFormProps) => {
  const [formData, setFormData] = useState<Partial<EasyTech.PlanningFilterType>>({
    status: []
  });

  useEffect(() => {
    if (props.data) {
      setFormData(props.data);
    } else {
      setFormData({
        status: []
      });
    }
  }, [props.data]);

  // Gestion des changements pour les champs simples
  const handleChange = (value: any, name: string) => {
    const tmp: Partial<EasyTech.PlanningFilterType> = structuredClone(formData);
    // Gestion spéciale pour le type "status", "technician" et "customer"
    if (name === "status") {
      tmp.status = Array.isArray(value) ? value : [value];
    } else if (name === "technician") {
      tmp.technician = value;
    } else if (name === "customer") {
      tmp.customer = value;
    } else {
      tmp[name as keyof Partial<EasyTech.PlanningFilterType>] = value;
    }
    props.onChange(name, tmp[name as keyof Partial<EasyTech.PlanningFilterType>]);
    setFormData(tmp);
  };

  const submit = (e: any) => {
    e.preventDefault();
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-planning-filter"}>
      <div className="grid grid-cols-1 gap-4"> 
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:technician:label")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "technician")}
            value={formData.technician || []}
            clearable={true}
            isMulti={true}
          />
        </div>
        
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:customer:label")}</label>
          <CustomerSelect
            onChange={(customers) => handleChange(customers, "customer")}
            value={formData.customer}
            isClearable={true}
            isMulti={true}
          />
        </div>
        
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:status:label")}</label>
          <WorkorderStatusSelect
            value={Array.isArray(formData.status) ? formData.status : (formData.status ? [formData.status] : [])}
            onChange={(values) => handleChange(values, "status")}
          />
        </div>
      </div>
    </form>
  );
};

export default PlanningFilterForm; 